import React, { forwardRef, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useIntersectionObserver } from '../../../hooks';

/**
 * SectionContent Component
 * 
 * A reusable component for displaying section content with titles and text blocks.
 * Supports smooth scroll animations using intersection observer.
 * 
 * @param {string} id - Unique identifier for the section
 * @param {string} title - Section title text
 * @param {string} titleVariant - MUI Typography variant for title (default: 'h2')
 * @param {string} titleColor - Color for the title (theme color string)
 * @param {Array} content - Array of content objects with text and optional styling
 * @param {boolean} animated - Enable/disable scroll animations (default: true)
 * @param {number|string} marginTop - Top margin for spacing above the section
 * @param {number|string} marginBottom - Bottom margin for spacing below the section
 * @param {Object} sx - Additional styling object
 * @param {ReactNode} children - Additional child components
 */
const SectionContent = forwardRef(({
  id,
  title,
  titleVariant = 'h2',
  titleColor,
  content = [],
  animated = true,
  marginTop,
  marginBottom,
  sx = {},
  children,
  ...props
}, ref) => {
  const theme = useTheme();
  const internalRef = useRef(null);
  const elementRef = ref || internalRef;
  const isVisible = useIntersectionObserver(elementRef, { threshold: 0.1, rootMargin: '-10px' });
  return (    <Box
      id={id}
      ref={elementRef}
      sx={{
        width: '100%',
        marginBottom: theme.spacing(0),
        ...(marginTop && { marginTop: typeof marginTop === 'number' ? theme.spacing(marginTop) : marginTop }),
        ...(marginBottom && { marginBottom: typeof marginBottom === 'number' ? theme.spacing(marginBottom) : marginBottom }),
        // Animation styles
        ...(animated && {
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          transition: `opacity 0.5s ease-out, transform 0.5s ease-out`,
        }),
        ...sx,
      }}
      {...props}
    >
      {/* Title */}
      {title && (        <Typography
          variant={titleVariant}
          component={titleVariant}          sx={{
            fontFamily: theme.typography.fontFamily,
            marginBottom: theme.spacing(2),            color: titleColor || theme.palette.text.primary,
            fontWeight: theme.typography.fontWeightSemiBold,
          }}
        >
          {title}
        </Typography>
      )}      {/* Content blocks */}
      {content.map((block, index) => {
        const {
          text,
          variant = 'body1',
          color = theme.palette.text.primary,
          align = 'left',
          marginBottom = theme.spacing(2),
        } = block;

        return (          <Typography
            key={index}
            variant={variant}
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontSize: '1.125rem', // 18px for better readability
              color,
              textAlign: align,
              marginBottom,
              lineHeight: 1.6,
            }}
          >
            {text}
          </Typography>
        );
      })}

      {/* Additional children */}
      {children}
    </Box>
  );
});

SectionContent.displayName = 'SectionContent';

export default SectionContent;
